import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { categoryMeta } from "../mock";
import { useEnabledCategoryNames } from "../hooks/useEnabledCategories";
import api from "../api";
import DealCard from "../components/dashboard/DealCard";
import { EmptyState } from "../components/dashboard/EmptyState";
import { useLocationFilter } from "../context/LocationContext";
import { distanceKm, formatDistance, radiusOptions } from "../utils/geo";
import { isLive } from "../utils/countdown";
import { isOpenNow, dealOpenState } from "../utils/hours";
import { fuzzyMatch, looseMatch, matchScore } from "../utils/fuzzy";
import { parseQuery } from "../utils/queryParse";
import { Navigation, X, Clock, Sparkles, Loader2, SearchX, Globe, Rocket, Leaf, TrendingUp, BellPlus, Check, BadgeCheck } from "lucide-react";
import { AI_ENABLED } from "../featureFlags";
import { Slider } from "../components/ui/slider";
import { useAuth } from "../context/AuthContext";
import { toast } from "../hooks/use-toast";
import { useLang } from "../context/LanguageContext";
import { TrustExplainer } from "../components/TrustExplainer";
import { catLabel } from "../utils/categoryLabel";
import { useSeo } from "../hooks/useSeo";
import { boostRank } from "../utils/boost";

const BrowseDeals = ({ title }) => {
  const routerLoc = useLocation();  const navigate = useNavigate();
  const [cat, setCat] = useState("All");
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [radiusIdx, setRadiusIdx] = useState(2); // default 25 km / 15 mi
  const [openNow, setOpenNow] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(() => localStorage.getItem("lp-verified-only") === "1");
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [shortDated, setShortDated] = useState(false);
  const [showFar, setShowFar] = useState(false);
  const [ai, setAi] = useState(null); // { summary, deals } from AI search
  const [aiLoading, setAiLoading] = useState(false);
  const { country, city, query, setQuery, gps, place, unit, setGps, toggleUnit } = useLocationFilter();
  const { user } = useAuth();
  const { t } = useLang();
  const isBusiness = !!user && user.role !== "shopper" && user.role !== "admin";
  const enabledCatNames = useEnabledCategoryNames();
  const catNames = ["All", ...enabledCatNames];

  const { km: RADII, labels: RADII_LABELS } = radiusOptions(unit);
  const parsed = useMemo(() => parseQuery(query || ""), [query]);
  const [didYouMean, setDidYouMean] = useState(null);
  const [trending, setTrending] = useState([]);
  const [savedQ, setSavedQ] = useState(false);
  const [semantic, setSemantic] = useState(null);
  const [semLoading, setSemLoading] = useState(false);
  const missRef = useRef("");
  useSeo({
    title: cat !== "All" ? `${catLabel(cat, t)} deals${city ? ` in ${city}` : country ? ` in ${country}` : ""}` : (title || t("browse.title")),
    description: cat !== "All"
      ? `Live ${catLabel(cat, t)} discounts${city ? ` in ${city}` : country ? ` in ${country}` : " near you"} — grab a deal and show your code in store.`
      : undefined,
  });

  // AI-assisted search: triggered when TopBar navigates here with an aiQuery
  useEffect(() => {
    const aiQuery = routerLoc.state?.aiQuery;
    if (!aiQuery) return;
    setAiLoading(true);
    setAi(null);
    const g = routerLoc.state?.gps || gps;
    if (!AI_ENABLED) { setAiLoading(false); return; }
    api.post("/search/ai", { q: aiQuery, lat: g?.lat, lng: g?.lng })
      .then((r) => setAi({ summary: r.data.summary, deals: (r.data.deals || []).filter(isLive), query: aiQuery }))
      .catch(() => setAi({ summary: t("browse.aiUnavailable"), deals: [], query: aiQuery }))
      .finally(() => setAiLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLoc.state?.aiQuery]);

  const clearAi = () => { setAi(null); navigate("/browse", { replace: true, state: {} }); };

  // Category tapped from a deal card (navigated here with state.category)
  useEffect(() => {
    const c = routerLoc.state?.category;
    if (c) {
      setCat(c);
      window.scrollTo({ top: 0, behavior: "smooth" });
      navigate(routerLoc.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLoc.state?.category]);

  useEffect(() => {
    setLoading(true);
    const params = { country };
    if (city) params.city = city;
    if (cat === "Upcoming") params.upcoming = true;
    api.get("/deals", { params: verifiedOnly ? { ...params, verifiedOnly: true } : params })
      .then((r) => setDeals(r.data.filter(isLive))).catch(() => {}).finally(() => setLoading(false));
  }, [country, city, cat, verifiedOnly]);

  const urlQ = new URLSearchParams(useLocation().search).get("q");
  useEffect(() => { if (urlQ) setQuery(urlQ); }, [urlQ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get(`/search/trending${country ? `?country=${encodeURIComponent(country)}` : ""}`)
      .then((r) => setTrending(r.data || [])).catch(() => setTrending([]));
  }, [country]);

  useEffect(() => {
    setSavedQ(false);
    if (!query || query.trim().length < 3) return;
    const id = setTimeout(() => {
      api.post("/search/log", { q: query, country }).catch(() => {});
    }, 1200);
    return () => clearTimeout(id);
  }, [query, country]);

  const saveSearch = async () => {
    if (!user) { toast({ title: t("saved.signIn") }); navigate("/signup"); return; }
    try {
      await api.post("/my/searches", { q: query });
      setSavedQ(true);
      toast({ title: t("saved.done", { q: query }), description: t("saved.doneBody") });
    } catch {
      toast({ title: t("common.tryAgain"), variant: "destructive" });
    }
  };

  const { list, relaxed, far } = useMemo(() => {
    let relaxedSearch = false;
    let out = deals;
    let outside = [];
    if (onlineOnly) out = out.filter((d) => d.isOnline);
    if (shortDated) out = out.filter((d) => d.shortDated);
    if (cat !== "All" && cat !== "Upcoming") {
      out = cat === "Near Expiry"
        ? out.filter((d) => d.nearExpiry)
        : out.filter((d) => categoryMeta(d.category).name === cat);
    }
    if (query) {
      if (parsed.text) {
        const blob = (d) => {
          const branchText = (d.branches || []).map((b) => `${b.label || ""} ${b.address || ""}`).join(" ");
          return `${d.title} ${d.business} ${d.category} ${d.location} ${branchText} ${d.description || ""}`;
        };
        const strict = out.filter((d) => fuzzyMatch(blob(d), parsed.text));
        // Rescue instead of a dead end: if nothing matches every word, keep deals matching ANY word.
        out = strict.length ? strict : out.filter((d) => looseMatch(blob(d), parsed.text));
        relaxedSearch = strict.length === 0 && out.length > 0;
      }
      if (parsed.minDiscount != null) out = out.filter((d) => (d.discount || 0) >= parsed.minDiscount);
      if (parsed.maxPrice != null) out = out.filter((d) => d.price != null && Number(d.price) <= parsed.maxPrice);
      if (parsed.withinDays != null) {
        const cutoff = Date.now() + parsed.withinDays * 86400000;
        out = out.filter((d) => d.expiresAt && new Date(d.expiresAt).getTime() <= cutoff);
      }
    }
    if (openNow || (query && parsed.openNow)) {
      out = out.filter((d) => {
        const bs = d.branches && d.branches.length ? d.branches : [null];
        return bs.some((b) => dealOpenState(d, b) !== "closed" && dealOpenState(d, b) !== "vacation");
      });
    }
    if (gps) {
      const radius = RADII[radiusIdx];
      const withDist = out.map((d) => {
        const bs = (d.branches && d.branches.length ? d.branches : [{ lat: d.lat, lng: d.lng }]).filter((b) => b.lat != null);
        const km = bs.length ? Math.min(...bs.map((b) => distanceKm(gps.lat, gps.lng, b.lat, b.lng))) : null;
        return { ...d, _distanceKm: km, _distanceLabel: km != null ? formatDistance(km, unit) : "", _branchCount: bs.length };
      });
      // Online deals have no pin, so distance never rules them out.
      out = withDist
        .filter((d) => d.isOnline || (d._distanceKm != null && d._distanceKm <= radius))
        .sort((a, b) => (a._distanceKm ?? 1e9) - (b._distanceKm ?? 1e9));
      // Anything beyond the cap is kept aside, never mixed into "near you".
      outside = withDist
        .filter((d) => !d.isOnline && d._distanceKm != null && d._distanceKm > radius)
        .sort((a, b) => a._distanceKm - b._distanceKm)
        .slice(0, 12);
    }
    const now = Date.now();
    // Boosted deals pin to the top — but when browsing by distance, only if they're actually nearby
    const isB = (d) => {
      if (!(d.boostedUntil && new Date(d.boostedUntil).getTime() > now)) return false;
      if (gps) {
        const r = RADII[radiusIdx];
        const cap = r === Infinity ? 25 : r; // don't pin far-away boosts when radius is "Any"
        return d._distanceKm != null && d._distanceKm <= cap;
      }
      return true;
    };
    // Relevance ranking: text match first when searching, then discount + rating + urgency + proximity
    const rel = (d) => {
      if (!parsed.text) return 0;
      return matchScore([
        [d.title, 5], [d.business, 3.2], [d.category, 2.2], [d.location, 1.4],
        [(d.branches || []).map((b) => `${b.label || ""} ${b.address || ""}`).join(" "), 1.2],
        [d.description || "", 1],
      ], parsed.text);
    };
    const score = (d) => {
      let s = d.discount || 0;
      if (d.businessRating) s += d.businessRating * 6;
      if (d.nearExpiry) s += 15;
      if (d._distanceKm != null) s += Math.max(0, 30 - d._distanceKm);
      return s;
    };
    out = [...out].sort((a, b) => {
      const bo = (isB(b) ? 1 : 0) - (isB(a) ? 1 : 0);
      if (bo !== 0) return bo;
      // Between two boosted deals, rotate hourly so every paying shop gets a turn on top.
      if (isB(a) && isB(b)) {
        const rot = boostRank(a.id) - boostRank(b.id);
        if (rot !== 0) return rot;
      }
      if (parsed.text) {
        const rd = rel(b) - rel(a);
        if (Math.abs(rd) > 0.12) return rd;   // clear relevance winner
      }
      return score(b) - score(a);
    });
    return { list: out, relaxed: relaxedSearch, far: outside };
  }, [deals, cat, query, parsed, gps, radiusIdx, openNow, onlineOnly, shortDated, unit]); // eslint-disable-line

  // Zero-result tracking + semantic (by-meaning) fallback
  useEffect(() => {
    if (loading || !query || list.length > 0) { setSemantic(null); return; }
    if (missRef.current === query) return;
    const t = setTimeout(() => {
      missRef.current = query;
      api.post("/search/miss", { q: query, city: city || "", country: country || "" }).catch(() => {});
      api.get(`/search/suggest?q=${encodeURIComponent(query)}`)
        .then((r) => setDidYouMean(r.data?.didYouMean || null))
        .catch(() => setDidYouMean(null));
      if (!AI_ENABLED) { setSemantic(null); return; }
      setSemLoading(true);
      api.post("/search/semantic", { q: query })
        .then((r) => setSemantic({ deals: (r.data.deals || []).filter(isLive) }))
        .catch(() => setSemantic({ deals: [] }))
        .finally(() => setSemLoading(false));
    }, 500);
    return () => clearTimeout(t);
  }, [loading, query, list.length]); // eslint-disable-line

  const placeLabel = place && place !== "your location" ? place : "your location";
  const isPickedPlace = !!place && place !== "your location";
  const shortPlace = isPickedPlace ? place.split(",")[0].trim() : "";

  if (aiLoading || ai) {
    return (
      <div className="space-y-6 lp-fade-up">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-extrabold text-foreground flex items-center gap-2" data-testid="browse-title"><Sparkles className="text-[#16a34a]" size={22} /> {t("bd.aiSearch")}</h1>
            <p className="text-sm text-muted-foreground" data-testid="ai-search-summary">{aiLoading ? `Understanding “${routerLoc.state?.aiQuery}”…` : ai.summary}</p>
          </div>
          <button onClick={clearAi} data-testid="clear-ai-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f] px-4 py-2 rounded-xl">{t("browse.clearSearch")} <X size={14} /></button>
        </div>
        {aiLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-10"><Loader2 className="animate-spin" size={18} /> {t("bd.findingTheBestMatches")}</div>
        ) : ai.deals.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5" data-testid="ai-deals-grid">
            {ai.deals.map((d) => (<DealCard key={d.id} deal={d} />))}
          </div>
        ) : (
          <p className="text-muted-foreground py-10">{t("browse.aiNoMatch")}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 lp-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground" data-testid="browse-title">{gps ? (isPickedPlace ? t("browse.nearPlace", { place: shortPlace }) : t("browse.nearYou")) : (title || t("browse.title"))}</h1>
          <p className="text-sm text-muted-foreground">{gps ? t("browse.sortedFrom", { place: placeLabel }) : (city || country) ? t("browse.bestLocal", { place: city || country }) : t("browse.worldwide")}</p>
        </div>
        {gps && (
          <button onClick={() => setGps(null)} data-testid="clear-near-me-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f] px-4 py-2 rounded-xl"><Navigation size={14} /> {isPickedPlace ? shortPlace : t("bd.nearMe")} <X size={14} /></button>
        )}
      </div>

      {gps && (
        <div className="bg-card border border-border rounded-2xl p-5" data-testid="radius-panel">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-foreground">{t("browse.radius")}</span>
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-[#16a34a]">Within {RADII_LABELS[radiusIdx]}</span>
              <div className="flex items-center bg-secondary rounded-lg p-0.5" data-testid="unit-toggle">
                {["km", "mi"].map((u) => (
                  <button key={u} onClick={() => { if (unit !== u) toggleUnit(); }} className={`px-2.5 py-1 text-xs font-bold rounded-md transition-colors ${unit === u ? "bg-[#16a34a] text-white" : "text-muted-foreground"}`} data-testid={`unit-${u}`}>{u}</button>
                ))}
              </div>
            </div>
          </div>
          <Slider value={[radiusIdx]} onValueChange={([v]) => setRadiusIdx(v)} min={0} max={RADII.length - 1} step={1} className="w-full" />
          <div className="flex justify-between mt-2">
            {RADII_LABELS.map((l, i) => (
              <button key={l} onClick={() => setRadiusIdx(i)} className={`text-xs font-medium ${i === radiusIdx ? "text-[#16a34a]" : "text-muted-foreground"}`}>{l}</button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {catNames.map((c) => (
          <button key={c} onClick={() => setCat(c)} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${cat === c ? "bg-[#16a34a] text-white shadow" : "bg-card border border-border text-muted-foreground hover:text-foreground"}`}>{catLabel(c, t)}</button>
        ))}
        <button
          onClick={() => setShortDated((v) => !v)}
          data-testid="short-dated-toggle"
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${shortDated ? "bg-[#16a34a] text-white shadow" : "bg-card border border-border text-muted-foreground hover:text-foreground"}`}
        >
          <Leaf size={15} /> {t("browse.shortDated")}
        </button>
        <button
          onClick={() => setOnlineOnly((v) => !v)}
          data-testid="online-only-toggle"
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${onlineOnly ? "bg-[#16a34a] text-white shadow" : "bg-card border border-border text-muted-foreground hover:text-foreground"}`}
        >
          <Globe size={15} /> {t("browse.online")}
        </button>
        <button
          onClick={() => { const v = !verifiedOnly; setVerifiedOnly(v); localStorage.setItem("lp-verified-only", v ? "1" : "0"); }}
          data-testid="verified-only-toggle"
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${verifiedOnly ? "bg-[#16a34a] text-white shadow" : "bg-card border border-border text-muted-foreground hover:text-foreground"}`}
        >
          <BadgeCheck size={15} /> {t("browse.verifiedOnly")}
        </button>
        <TrustExplainer testid="browse-trust-explainer" />
        <button
          onClick={() => setOpenNow((v) => !v)}
          data-testid="open-now-toggle"
          className={`ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${openNow ? "bg-[#16a34a] text-white shadow" : "bg-card border border-border text-muted-foreground hover:text-foreground"}`}
        >
          <Clock size={15} /> {t("browse.openNow")}
        </button>
      </div>

      {!query && trending.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="trending-searches">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            <TrendingUp size={13} className="text-[#16a34a]" /> {t("trending.label")}
          </span>
          {trending.map((tr) => (
            <button key={tr.q} onClick={() => setQuery(tr.q)} data-testid={`trending-${tr.q.replace(/\s+/g, "-")}`}
              className="px-3 py-1.5 rounded-full text-sm font-semibold bg-card border border-border text-foreground hover:border-[#16a34a] hover:bg-[#e7f6ec] dark:hover:bg-[#16341f] transition-colors">
              {tr.q}
            </button>
          ))}
        </div>
      )}

      {query && list.length > 0 && (
        <div data-testid="save-search-row">
          <button onClick={saveSearch} disabled={savedQ} data-testid="save-search-btn"
            className={`inline-flex items-center gap-1.5 text-sm font-bold px-3.5 py-2 rounded-xl border transition-colors ${savedQ ? "bg-[#e7f6ec] dark:bg-[#16341f] border-[#16a34a]/40 text-[#0e7a3c] dark:text-[#7ee0a3]" : "bg-card border-border text-foreground hover:border-[#16a34a] hover:text-[#0e7a3c]"}`}>
            {savedQ ? <Check size={15} /> : <BellPlus size={15} />}
            {savedQ ? t("saved.saved") : t("saved.cta")}
          </button>
        </div>
      )}

      {query && (parsed.maxPrice != null || parsed.minDiscount != null || parsed.openNow || parsed.nearMe || parsed.withinDays != null) && (
        <div className="flex flex-wrap items-center gap-2" data-testid="smart-filters">
          <span className="text-xs font-semibold text-muted-foreground">{t("browse.searchFilters")}</span>
          {parsed.maxPrice != null && <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#e7f6ec] text-[#0e6b3e] dark:bg-[#16341f] dark:text-[#7ee0a3]" data-testid="smart-filter-price">under ${parsed.maxPrice}</span>}
          {parsed.openNow && <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#e7f6ec] text-[#0e6b3e] dark:bg-[#16341f] dark:text-[#7ee0a3]" data-testid="smart-filter-open">{t("bd.openNow")}</span>}
          {parsed.minDiscount != null && <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#e7f6ec] text-[#0e6b3e] dark:bg-[#16341f] dark:text-[#7ee0a3]" data-testid="smart-filter-discount">{parsed.minDiscount}%+ off</span>}
          {parsed.nearMe && <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#e7f6ec] text-[#0e6b3e] dark:bg-[#16341f] dark:text-[#7ee0a3]" data-testid="smart-filter-near">{t("bd.nearMe2")}</span>}
          {parsed.withinDays != null && <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#e7f6ec] text-[#0e6b3e] dark:bg-[#16341f] dark:text-[#7ee0a3]" data-testid="smart-filter-window">ends {parsed.windowLabel}</span>}
        </div>
      )}

      {query && !loading && (relaxed || (didYouMean && list.length === 0)) && (
        <div className="flex flex-wrap items-center gap-2 bg-[#fffbeb] dark:bg-[#3b2a08] border border-[#f59e0b]/40 rounded-xl px-3.5 py-2.5" data-testid="search-rescue">
          <SearchX size={15} className="text-[#b45309]" />
          <span className="text-sm text-[#7c4a03] dark:text-[#fbbf24]">
            {relaxed ? t("browse.closestMatches", { q: query }) : t("browse.noMatch", { q: query })}
          </span>
          {didYouMean && (
            <button onClick={() => setQuery(didYouMean)} data-testid="did-you-mean"
              className="text-sm font-bold text-[#0e7a3c] dark:text-[#7ee0a3] underline decoration-dotted underline-offset-4">
              {t("browse.didYouMean", { q: didYouMean })}
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5">
          {Array.from({ length: 4 }).map((_, i) => (<div key={i} className="h-64 rounded-2xl bg-secondary animate-pulse" />))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5" data-testid="deals-grid">
          {list.map((d) => (<DealCard key={d.id} deal={d} />))}
        </div>
      )}
      {!loading && list.length === 0 && query && (
        semLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-10" data-testid="semantic-loading"><Loader2 className="animate-spin" size={18} /> {t("bd.noExactMatchesSearching")}</div>
        ) : semantic?.deals?.length ? (
          <div className="space-y-4" data-testid="semantic-results">
            <p className="text-sm text-muted-foreground flex items-center gap-2"><Sparkles size={16} className="text-[#16a34a]" /> No exact matches for “{query}” — here are the closest matches by meaning:</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5">
              {semantic.deals.map((d) => (<DealCard key={d.id} deal={d} />))}
            </div>
          </div>
        ) : (
          <EmptyState
            icon={SearchX}
            title={t("browse.noDeals")}
            message={t("browse.noMatch", { q: query })}
            action={<Link to="/categories" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">{t("browse.browseCats")}</Link>}
          />
        )
      )}
      {!loading && gps && far.length > 0 && (
        <div className="space-y-4 pt-2" data-testid="further-afield">
          <button onClick={() => setShowFar((v) => !v)} data-testid="further-afield-toggle"
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-bold text-muted-foreground transition-colors hover:border-[#16a34a] hover:text-[#16a34a]">
            <Globe size={15} /> {showFar ? t("browse.hideFurther") : t("browse.showFurther", { n: far.length })}
          </button>
          {showFar && (
            <>
              <p className="text-xs text-muted-foreground" data-testid="further-afield-note">
                {t("browse.furtherNote", { radius: RADII_LABELS[radiusIdx] })}
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5" data-testid="further-deals-grid">
                {far.map((d) => (<DealCard key={d.id} deal={d} />))}
              </div>
            </>
          )}
        </div>
      )}
      {!loading && list.length === 0 && !query && gps && far.length > 0 && (
        <EmptyState
          icon={Navigation}
          title={t("browse.nothingWithin", { radius: RADII_LABELS[radiusIdx] })}
          message={t("browse.nothingWithinMsg", { radius: RADII_LABELS[radiusIdx] })}
          testId="nothing-nearby"
          action={(
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button onClick={() => setShowFar(true)} data-testid="show-further-btn"
                className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">
                <Globe size={16} /> {t("browse.showFurther", { n: far.length })}
              </button>
              <button onClick={() => setGps(null)} data-testid="browse-country-btn"
                className="inline-flex items-center gap-2 bg-card border border-border text-foreground text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors hover:border-[#16a34a]">
                {t("browse.browseCountry")}
              </button>
            </div>
          )}
        />
      )}
      {!loading && list.length === 0 && !query && !(gps && far.length > 0) && (
        isBusiness && cat === "All" && !openNow && !onlineOnly && !shortDated ? (
          <EmptyState
            icon={Rocket}
            title={t("browse.firstBusiness", { place: city || country || t("browse.yourArea") })}
            message={t("browse.firstBusinessMsg")}
            testId="cold-start-business"
            action={<Link to="/post-deal" data-testid="cold-start-post-btn" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"><Rocket size={16} /> {t("browse.postFirst")}</Link>}
          />
        ) : (
        <EmptyState
          icon={SearchX}
          title={t("browse.noDeals")}
          message={verifiedOnly ? t("browse.emptyVerified") : openNow ? t("browse.emptyOpenNow") : shortDated ? t("browse.emptyShortDated") : onlineOnly ? t("browse.emptyOnline") : gps ? (isPickedPlace ? t("browse.emptyRadiusPlace", { radius: RADII_LABELS[radiusIdx], place: shortPlace }) : t("browse.emptyRadius", { radius: RADII_LABELS[radiusIdx] })) : t("browse.emptyDefault")}
          action={<Link to="/categories" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">{t("browse.browseCats")}</Link>}
        />
        )
      )}
    </div>
  );
};

export default BrowseDeals;
